import React, { useEffect, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const TRIGGER_OPTIONS = [
  { value: 'time_tick', label: 'Time tick (default)' },
  { value: 'wicket', label: 'Wicket' },
  { value: 'half_century', label: 'Half century' },
  { value: 'century', label: 'Century' },
  { value: 'fifty_partnership', label: '50 partnership' },
  { value: 'hundred_partnership', label: '100 partnership' },
  { value: 'hundred_team', label: 'Team 100' },
  { value: 'two_hundred_team', label: 'Team 200' },
  { value: 'three_hundred_team', label: 'Team 300' },
  { value: 'after_toss', label: 'After toss' },
  { value: 'big_over', label: 'Big over' },
];

function getOptionalAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function AiMatchSummary() {
  const [rows, setRows] = useState([]);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoadingMatchId, setActionLoadingMatchId] = useState(null);
  const [expandedMatchId, setExpandedMatchId] = useState(null);

  const [providers, setProviders] = useState([]);
  const [genAiOptionsLoading, setGenAiOptionsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedTrigger, setSelectedTrigger] = useState('time_tick');

  const [triggerMatchId, setTriggerMatchId] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = total > 0 ? Math.ceil(total / limit) : 1;

  useEffect(() => { fetchGenAIOptions(); }, []);
  useEffect(() => {
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset]);

  const fetchGenAIOptions = async () => {
    setGenAiOptionsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/genai/options`, {
        method: 'GET',
        credentials: 'include',
        headers: getOptionalAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.data?.providers) {
        setProviders(Array.isArray(data.data.providers) ? data.data.providers : []);
      }
    } catch {
      // non-blocking
    } finally {
      setGenAiOptionsLoading(false);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${BASE_URL}/genai/match-summary/matches?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getOptionalAuthHeaders() },
        },
      );
      if (res.status === 401 || res.status === 403) {
        setError('Unauthorized. Please log in again.');
        setRows([]);
        setTotal(0);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to load AI match summaries');
        setRows([]);
        setTotal(0);
        return;
      }
      const list = data.data?.matches ?? [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(list.length); // backend doesn't return a count yet; treat full page as "more available"
    } catch (err) {
      setError(err?.message || 'Failed to load AI match summaries');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (direction) => {
    if (direction === 'prev') {
      setOffset((prev) => Math.max(prev - limit, 0));
    } else if (direction === 'next') {
      // Page forward whenever the current page is full; if the next page is
      // empty the table will say so and Next will disable.
      if (rows.length >= limit) setOffset(offset + limit);
    }
  };

  const getLatestSummary = (m) => {
    const list = m.summaries;
    if (Array.isArray(list) && list.length > 0) return list[0];
    return {
      line: m.ai_match_summary ?? null,
      llm_model: m.ai_match_summary_llm_model ?? null,
      llm_input_tokens: m.ai_match_summary_llm_input_tokens ?? null,
      llm_output_tokens: m.ai_match_summary_llm_output_tokens ?? null,
      trigger_type: m.ai_match_summary_trigger_type ?? null,
      created_at: m.ai_match_summary_updated_at ?? null,
    };
  };

  const buildTriggerBody = () => {
    const body = { trigger: selectedTrigger || 'time_tick' };
    if (selectedProvider) body.provider = selectedProvider;
    if (selectedModel) body.model = selectedModel;
    return body;
  };

  const handleRegenerate = async (matchId) => {
    if (!matchId) return;
    setError('');
    setSuccess('');
    setActionLoadingMatchId(matchId);
    try {
      const res = await fetch(
        `${BASE_URL}/genai/match-summary/match/${matchId}/trigger`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getOptionalAuthHeaders() },
          body: JSON.stringify(buildTriggerBody()),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to regenerate match summary');
        return;
      }
      const payload = data.data || {};
      setSuccess('Match summary generated');
      setRows((prev) =>
        prev.map((m) => {
          if (m.match_id !== payload.matchId) return m;
          const newSummary = {
            line: payload.line,
            llm_model: payload.llm_model ?? null,
            llm_input_tokens: payload.llm_input_tokens ?? null,
            llm_output_tokens: payload.llm_output_tokens ?? null,
            trigger_type: payload.trigger_type ?? null,
            created_at: payload.created_at,
          };
          const existing = Array.isArray(m.summaries) ? m.summaries : [];
          return {
            ...m,
            summaries: [newSummary, ...existing],
            ai_match_summary: payload.line,
            ai_match_summary_llm_model: payload.llm_model,
            ai_match_summary_llm_input_tokens: payload.llm_input_tokens,
            ai_match_summary_llm_output_tokens: payload.llm_output_tokens,
            ai_match_summary_trigger_type: payload.trigger_type,
            ai_match_summary_updated_at: payload.created_at,
          };
        }),
      );
    } catch (err) {
      setError(err?.message || 'Failed to regenerate match summary');
    } finally {
      setActionLoadingMatchId(null);
    }
  };

  const handleTriggerByMatchId = async () => {
    const matchId = triggerMatchId.trim();
    if (!matchId) {
      setError('Enter a Match ID to trigger generation.');
      return;
    }
    setError('');
    setSuccess('');
    setTriggerLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/genai/match-summary/match/${matchId}/trigger`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getOptionalAuthHeaders() },
          body: JSON.stringify(buildTriggerBody()),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Failed to generate match summary');
        return;
      }
      setSuccess(`Match summary generated for match ${matchId}`);
      setTriggerMatchId('');
      fetchMatches();
    } catch (err) {
      setError(err?.message || 'Failed to generate match summary');
    } finally {
      setTriggerLoading(false);
    }
  };

  const selectedProviderObj = providers.find((p) => p.id === selectedProvider);
  const models = selectedProviderObj?.models ?? [];

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-6">
                <h3>AI Match Summary (2–4 sentence prose)</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 2,
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Generate by Match ID
                    </Typography>
                    <TextField
                      label="Match ID"
                      size="small"
                      type="number"
                      value={triggerMatchId}
                      onChange={(e) => setTriggerMatchId(e.target.value)}
                      placeholder="e.g. 94714"
                      sx={{ width: 120 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Trigger</InputLabel>
                      <Select
                        value={selectedTrigger}
                        label="Trigger"
                        onChange={(e) => setSelectedTrigger(e.target.value)}
                      >
                        {TRIGGER_OPTIONS.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }} disabled={genAiOptionsLoading}>
                      <InputLabel>Provider</InputLabel>
                      <Select
                        value={selectedProvider}
                        label="Provider"
                        onChange={(e) => {
                          setSelectedProvider(e.target.value);
                          setSelectedModel('');
                        }}
                      >
                        <MenuItem value="">Default</MenuItem>
                        {providers.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedProvider || genAiOptionsLoading}>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={selectedModel}
                        label="Model"
                        onChange={(e) => setSelectedModel(e.target.value)}
                      >
                        <MenuItem value="">Default</MenuItem>
                        {(models || []).map((mod) => (
                          <MenuItem key={mod.id} value={mod.id}>{mod.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleTriggerByMatchId}
                      disabled={triggerLoading}
                    >
                      {triggerLoading ? <CircularProgress size={20} color="inherit" /> : 'Trigger Generation'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Trigger / provider / model above also apply to Regenerate in the table.
                    The cron also auto-fires on milestones for live matches.
                  </Typography>

                  {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                  {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={40} />
                            <TableCell>Match ID</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Teams</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell sx={{ minWidth: 320 }}>Latest summary</TableCell>
                            <TableCell>Trigger</TableCell>
                            <TableCell>Model</TableCell>
                            <TableCell>Tokens (in/out)</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={11} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No matches with summaries yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            rows.map((m) => {
                              const latest = getLatestSummary(m);
                              const summariesList = Array.isArray(m.summaries) ? m.summaries : [];
                              const hasHistory = summariesList.length > 1;
                              const isExpanded = expandedMatchId === m.match_id;
                              return (
                                <React.Fragment key={m.match_id}>
                                  <TableRow>
                                    <TableCell>
                                      {hasHistory ? (
                                        <IconButton
                                          size="small"
                                          onClick={() => setExpandedMatchId(isExpanded ? null : m.match_id)}
                                        >
                                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                      ) : null}
                                    </TableCell>
                                    <TableCell>{m.match_id}</TableCell>
                                    <TableCell>{m.title}</TableCell>
                                    <TableCell>{m.teama__short_name} vs {m.teamb__short_name}</TableCell>
                                    <TableCell>{m.status_str}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {latest?.line || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {latest?.trigger_type
                                        ? <Chip size="small" label={latest.trigger_type} />
                                        : <Typography variant="caption">—</Typography>}
                                    </TableCell>
                                    <TableCell><Typography variant="caption">{latest?.llm_model || '—'}</Typography></TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {typeof latest?.llm_input_tokens === 'number' ? latest.llm_input_tokens : '-'}
                                        {' '}/{' '}
                                        {typeof latest?.llm_output_tokens === 'number' ? latest.llm_output_tokens : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">
                                        {latest?.created_at ? new Date(latest.created_at).toLocaleString() : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleRegenerate(m.match_id)}
                                        disabled={actionLoadingMatchId === m.match_id}
                                      >
                                        {actionLoadingMatchId === m.match_id ? (
                                          <CircularProgress size={16} color="inherit" />
                                        ) : latest?.line ? 'Regenerate' : 'Generate'}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                  {hasHistory && (
                                    <TableRow>
                                      <TableCell colSpan={11} sx={{ py: 0, borderBottom: 0 }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ py: 1, pl: 4 }}>
                                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                              History ({summariesList.length} summaries)
                                            </Typography>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell sx={{ minWidth: 320 }}>Summary</TableCell>
                                                  <TableCell>Trigger</TableCell>
                                                  <TableCell>Model</TableCell>
                                                  <TableCell>In / Out tokens</TableCell>
                                                  <TableCell>Created</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {summariesList.map((u, idx) => (
                                                  <TableRow key={idx}>
                                                    <TableCell>
                                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {u.line || '—'}
                                                      </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                      {u.trigger_type
                                                        ? <Chip size="small" label={u.trigger_type} />
                                                        : '—'}
                                                    </TableCell>
                                                    <TableCell>{u.llm_model || '—'}</TableCell>
                                                    <TableCell>{u.llm_input_tokens ?? '-'} / {u.llm_output_tokens ?? '-'}</TableCell>
                                                    <TableCell>{u.created_at ? new Date(u.created_at).toLocaleString() : '—'}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Page {page} of {totalPages}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={offset === 0 || loading}
                        onClick={() => handleChangePage('prev')}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={rows.length < limit || loading}
                        onClick={() => handleChangePage('next')}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default AiMatchSummary;
